import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "@phosphor-icons/react";

const flagKey = (id) => `pr.starthere.${id}`;
const readFlag = (id) => { try { return localStorage.getItem(flagKey(id)) === "1"; } catch { return false; } };
const writeFlag = (id) => { try { localStorage.setItem(flagKey(id), "1"); } catch { /* noop */ } };

// The post-setup path. The onboarding checklist gets you connected; this one
// shows you what to actually DO in your first relay.
export const StartHereCard = ({ summary, onFocusLog }) => {
  const [touched, setTouched] = useState(0);
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem("pr.starthere.dismissed") === "1"; } catch { return false; } });

  const logged = !!summary.has_logged_ever || (summary.earned || 0) > 0;
  const closed = (summary.streak?.completed || 0) > 0 || (summary.streak?.cleared || 0) > 0;

  const steps = [
    {
      id: "log",
      title: "Log a day of income",
      body: "Any amount you've earned today. One entry starts your relay and your streak.",
      done: logged,
      action: { label: "Log a win →", onClick: onFocusLog },
    },
    {
      id: "timeoff",
      title: "Tag a day off",
      body: "Taking a day? Tag it and your target adjusts, a day off never breaks your streak.",
      done: readFlag("timeoff"),
      action: { label: "Open Time Off →", to: "/app/timeoff", mark: "timeoff" },
    },
    {
      id: "close",
      title: "Close your first relay",
      body: "At the end of your relay week you'll confirm what you actually earned. That's how a relay gets cleared.",
      done: closed,
    },
    summary.sheet_url && {
      id: "sheet",
      title: "Open your Google Sheet once",
      body: "Your season record, in your own Drive. You log in the app and it syncs to the sheet for you.",
      done: readFlag("sheet"),
      action: { label: "Open my sheet →", href: summary.sheet_url, mark: "sheet" },
    },
  ].filter(Boolean);

  const done = steps.filter((st) => st.done).length;
  if (hidden || done === steps.length) return null;
  void touched;

  const mark = (id) => { if (id) { writeFlag(id); setTouched((n) => n + 1); } };
  const dismiss = () => {
    setHidden(true);
    try { localStorage.setItem("pr.starthere.dismissed", "1"); } catch { /* noop */ }
  };

  const btn = "inline-block mt-2 px-4 py-2 rounded-full text-xs font-bold pr-btn-primary";

  return (
    <div className="rounded-2xl p-5 lg:p-6 pr-card" data-testid="start-here-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--pr-muted)" }} data-testid="start-here-progress">
            Start here · {done} of {steps.length} done
          </p>
          <h2 className="text-lg lg:text-xl mt-0.5 mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "var(--pr-ink)" }}>
            Your first relay, step by step.
          </h2>
        </div>
        <button onClick={dismiss} className="p-0.5 flex-shrink-0" aria-label="Dismiss" data-testid="start-here-dismiss">
          <X className="w-4 h-4" style={{ color: "var(--pr-muted)" }} />
        </button>
      </div>

      <ol className="space-y-2">
        {steps.map((st, i) => (
          <li key={st.id} className="flex items-start gap-3 py-2" data-testid={`start-here-step-${st.id}`}>
            <span
              className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold mt-0.5"
              style={{
                background: st.done ? "var(--pr-green-surface)" : "transparent",
                border: st.done ? "none" : "1px solid var(--pr-line)",
                color: st.done ? "#fff" : "var(--pr-ink2)",
              }}
            >
              {st.done ? <Check className="w-3 h-3" /> : i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold" style={{ color: "var(--pr-ink)", opacity: st.done ? 0.55 : 1 }}>{st.title}</p>
              <p className="text-xs leading-relaxed mt-0.5" style={{ color: "var(--pr-ink2)", opacity: st.done ? 0.55 : 1 }}>{st.body}</p>
              {!st.done && st.action && (
                st.action.href ? (
                  <a href={st.action.href} target="_blank" rel="noreferrer" onClick={() => mark(st.action.mark)} className={btn} data-testid={`start-here-cta-${st.id}`}>
                    {st.action.label}
                  </a>
                ) : st.action.to ? (
                  <Link to={st.action.to} onClick={() => mark(st.action.mark)} className={btn} data-testid={`start-here-cta-${st.id}`}>
                    {st.action.label}
                  </Link>
                ) : (
                  <button type="button" onClick={st.action.onClick} className={btn} data-testid={`start-here-cta-${st.id}`}>
                    {st.action.label}
                  </button>
                )
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default StartHereCard;
